<template>
    <div class="flex flex-col gap-2">
        <!-- Selected connection -->
        <div v-if="selectedConnection" class="flex items-start justify-between gap-2">
            <div class="flex flex-col gap-1">
                <p class="nadia-title-subsection text-ink">{{ selectedConnection.name }}</p>
                <p v-if="selectedConnection.job_title" class="body-small text-ink-muted">{{ selectedConnection.job_title }}</p>
            </div>
            <BaseButton theme="tertiary" size="sm" :disabled="disabled" @click="handleClear">{{ t("journeys.connectionSearch.change") }}</BaseButton>
        </div>

        <!-- Search -->
        <template v-else>
            <div ref="searchField">
                <FormField :id="inputId" :label="t('journeys.connectionSearch.label')" hide-label>
                    <BaseCombobox
                        v-model:input="searchQuery"
                        :model-value="selectedConnection"
                        :options="options"
                        :display-value="(connection) => connection.name ?? ''"
                        :key-value="(connection) => connection.id"
                        :input-id="inputId"
                        :auto-focus="autoFocus"
                        :disabled="disabled"
                        :is-loading-more="isSearching"
                        disable-portal
                        ignore-filter
                        :placeholder="t('journeys.connectionSearch.placeholder')"
                        @update:model-value="handleSelection">
                        <template #item="{ option }">
                            <span class="flex flex-col gap-1">
                                <span class="body-small-semibold text-ink">{{ option.name }}</span>
                                <span v-if="option.job_title" class="details text-ink-muted">{{ option.job_title }}</span>
                            </span>
                        </template>
                        <template #empty>
                            <p v-if="showNoResults" class="body-small text-ink-muted p-2">{{ t("journeys.connectionSearch.noResults") }}</p>
                        </template>
                        <template #loading-more>{{ t("journeys.connectionSearch.searching") }}</template>
                    </BaseCombobox>
                </FormField>
            </div>
            <!-- A plain anchor, not BaseLink: it opens in a new tab, and Inertia ignores target on its own links. -->
            <I18nT keypath="journeys.connectionSearch.addFromPeople" scope="global" tag="p" class="details text-ink-muted px-1 pt-2 border-t border-border mt-1">
                <template #link
                    ><a href="/people" target="_blank" class="base-link">{{ t("journeys.connectionSearch.addFromPeopleLink") }}</a></template
                >
            </I18nT>
        </template>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import FormField from "~vue/components/form/FormField.vue";
import { computed, onBeforeUnmount, ref, shallowRef, useId, watch } from "vue";
import { I18nT, useI18n } from "vue-i18n";

const { t } = useI18n();

interface ConnectionResult {
    id: string;
    connection: { job_title: string | null };
    persona: { name: string };
}

interface ConnectionListResponse {
    connections: ConnectionResult[];
    has_more: boolean;
}

export interface SelectedConnection {
    id: string;
    name: string | null;
    job_title: string | null;
}

const props = defineProps<{
    autoFocus?: boolean;
    disabled?: boolean;
}>();

const emit = defineEmits<{
    select: [connection: SelectedConnection];
    clear: [];
}>();

const searchField = shallowRef<HTMLElement | null>(null);
const inputId = useId();
const searchQuery = ref("");
const isDebouncing = ref(false);
const selectedConnection = ref<SelectedConnection | null>(null);

const searchUrl = computed(() => `/v1/connections/?search=${encodeURIComponent(searchQuery.value)}&page_size=10`);
const { data: searchData, execute: runSearch, isFetching } = useFetch(searchUrl, { immediate: false }).json<ConnectionListResponse>();
const isSearching = computed(() => isDebouncing.value || isFetching.value);
const options = computed<SelectedConnection[]>(() => {
    if (!searchQuery.value.trim() || isSearching.value) {
        return [];
    }
    return (searchData.value?.connections ?? []).map((connection) => ({
        id: connection.id,
        name: connection.persona.name,
        job_title: connection.connection.job_title,
    }));
});
const showNoResults = computed(() => searchQuery.value.trim().length > 0 && !isSearching.value && options.value.length === 0);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
function clearDebounce() {
    if (debounceTimer !== null) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
    }
}

watch(searchQuery, (val) => {
    clearDebounce();
    if (!val.trim()) {
        isDebouncing.value = false;
        return;
    }
    isDebouncing.value = true;
    debounceTimer = setTimeout(() => {
        debounceTimer = null;
        isDebouncing.value = false;
        void runSearch();
    }, 300);
});
onBeforeUnmount(clearDebounce);

function clearSearch() {
    clearDebounce();
    searchQuery.value = "";
    isDebouncing.value = false;
}

function handleSelection(connection: SelectedConnection | null | undefined) {
    if (props.disabled) {
        return;
    }
    if (connection == null) {
        handleClear();
        return;
    }
    selectedConnection.value = connection;
    clearSearch();
    emit("select", connection);
}

function handleClear() {
    if (props.disabled) {
        return;
    }
    selectedConnection.value = null;
    clearSearch();
    emit("clear");
}

function reset() {
    selectedConnection.value = null;
    clearSearch();
}

defineExpose({ reset, focus: () => searchField.value?.querySelector<HTMLInputElement>('input[role="combobox"]')?.focus() });
</script>
